import type { Entity, EntityReference } from "@firecms/core";
import type { Event } from "../collections/events";
import type { EventCollectionId } from "./collectionIds";

const EVENT_PATH_RE =
  /^accounts\/([^/]+)\/events(?:\/([^/]+))?$/;

export function parseEventPath(eventPath: string): {
  accountId: string;
  spaceId: string;
  eventId: string;
} | null {
  const match = eventPath.match(EVENT_PATH_RE);
  if (!match?.[1]) {
    return null;
  }

  return {
    accountId: match[1],
    spaceId: "",
    eventId: match[2] ?? "",
  };
}

export function getAccountIdFromEventPath(eventPath: string): string {
  return parseEventPath(eventPath)?.accountId ?? "";
}

export function getSpaceIdFromEventPath(eventPath: string): string {
  return parseEventPath(eventPath)?.spaceId ?? "";
}

export function getEventIdFromEventPath(eventPath: string): string {
  return parseEventPath(eventPath)?.eventId ?? "";
}

export function spaceIdFromEventEntity(
  entity: Entity<Event> | undefined
): string {
  const space = entity?.values.space;
  if (!space) {
    return "";
  }
  if (typeof space === "object" && "id" in space && space.id) {
    return String(space.id);
  }
  return "";
}

export function spaceIdFromEventReference(eventRef: EntityReference): string {
  return getSpaceIdFromEventPath(eventRef.path);
}

export function toEventCollectionId(
  eventPath: string,
  eventId?: string
): EventCollectionId | null {
  const parsed = parseEventPath(eventPath);
  if (!parsed) {
    return null;
  }

  const resolvedEventId = eventId ?? parsed.eventId;
  if (!resolvedEventId) {
    return null;
  }

  return {
    accountId: parsed.accountId,
    spaceId: parsed.spaceId,
    eventId: resolvedEventId,
  };
}

export function toEventCollectionIdFromEntity(
  entity: Entity<Event>
): EventCollectionId | null {
  const spaceId =
    spaceIdFromEventEntity(entity) ||
    getSpaceIdFromEventPath(entity.path);

  return {
    accountId: getAccountIdFromEventPath(entity.path),
    spaceId,
    eventId: entity.id,
  };
}
